import React, { useEffect, useState, useMemo, useRef } from "react";
import { Wrapper } from "./style";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import lodashDebounce from "lodash.debounce";

import Image from "next/image";
import Link from "components/common/Link";
import HamburgerMenu from "./HamburgerMenu";
import Register from "components/templates/register";
import Setting from "components/templates/profile/Setting";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import PhoneEnabledIcon from "@mui/icons-material/PhoneEnabled";
import MedicationIcon from "@mui/icons-material/Medication";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import ChooseLanguage from "./ChooseLanguage";
import Loader from "../../DotLoader";

import { appConfig } from "configs";
import ModalManager from "libs/ModalManager";
import Translation from "libs/Translation";

const Header = ({ webConfig, chooseLanguage }) => {
  const router = useRouter();
  const { user, authentication } = useSelector((state) => state.user);
  const isAuth = !!authentication?.accessToken;

  // 1. Create a ref to track the previous auth state
  const prevAuthRef = useRef(isAuth);

  // 2. Watch for changes in isAuth
  useEffect(() => {
    // Check if user transitioned from 'Not Logged In' (false) to 'Logged In' (true)
    if (!prevAuthRef.current && isAuth) {
      window.location.reload();
    }
    // Update the ref to the current state for the next render
    prevAuthRef.current = isAuth;
  }, [isAuth]);

  const [isRouteChanging, setIsRouteChanging] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);

    if (!router?.events) return;

    const handleStart = (url) => {
      if (url !== router.asPath) {
        setIsRouteChanging(true);
      }
    };

    const handleDone = () => {
      setIsRouteChanging(false);
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleDone);
    router.events.on("routeChangeError", handleDone);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleDone);
      router.events.off("routeChangeError", handleDone);
    };
  }, [router]);

  const items = [
    {
      id: 0,
      icon: <HomeIcon />,
      label: Translation.t("label.main-page"),
      link: "/",
      params: webConfig.lang !== "fa" ? { lang: webConfig.lang } : {},
    },
    {
      id: 2,
      icon: <MedicationIcon />,
      label: Translation.t("label.doctors-page"),
      link: "/explore/doctors",
      params: webConfig.lang !== "fa" ? { lang: webConfig.lang } : {},
    },
    {
      id: 3,
      icon: <PhoneEnabledIcon />,
      label: Translation.t("label.contact-page"),
      link: "/contact",
      params: webConfig.lang !== "fa" ? { lang: webConfig.lang } : {},
    },
    {
      id: 4,
      icon: <InfoIcon />,
      label: Translation.t("label.about-us-page"),
      link: "/about",
      params: webConfig.lang !== "fa" ? { lang: webConfig.lang } : {},
    },
    {
      id: 6,
      desktopOnly: true,
      label: Translation.t("label.download-app"),
      link: "/landing",
      params: webConfig.lang !== "fa" ? { lang: webConfig.lang } : {},
    },
    {
      id: 7,
      icon: <ImportContactsIcon />,
      label: Translation.t("label.blog-page"),
      link: "https://vclinic.io/blog",
    },
  ];

  // Moved button definitions here so they are accessible to handleOpenMenu
  // But we will use a derived variable for the actual render
  const pushToRoute = () => {
    if (webConfig.lang !== "fa") {
      router.push(`${appConfig.site_url}/hwp?lang=${webConfig.lang}`);
      ModalManager.hide();
    } else {
      router.push(`${appConfig.site_url}/hwp`);
      ModalManager.hide();
    }
  };

  const openModal = () => {
    if (isAuth) {
      ModalManager.show({
        content: <Setting webConfig={webConfig} />,
        title: Translation.t("title.logged-in-with", {
          phone: Translation.n(`09${user.phone.slice(2)}`),
        }),
        maxWidth: "sm",
        fullScreenMobile: false,
        hideCloseButton: false,
        closable: true,
      });
    } else {
      ModalManager.show({
        content: <Register webConfig={webConfig} />,
        title: "",
        maxWidth: "sm",
        fullScreenMobile: true,
        hideCloseButton: false,
        closable: true,
      });
    }
  };

  const debouncedOpenModal = lodashDebounce(() => {
    openModal();
  }, 100);

  const handleOpenModal = () => {
    ModalManager.hide();
    debouncedOpenModal();
  };

  // Calculate buttons dynamically to avoid Hydration Mismatch
  const buttons = useMemo(() => {
    // Only show user profile if we have mounted AND we have user data
    const showProfile = hasMounted && user?.profiles?.length;

    const profileButton = {
      id: 0,
      label: showProfile
        ? user?.profiles[0]?.FirstName
          ? `${user?.profiles[0]?.FirstName} ${user?.profiles[0]?.LastName}`
          : user?.profiles[0]?.phoneNumber
        : webConfig.lang !== "fa"
        ? "Client Login"
        : "ورود مراجعه‌کننده",
      onClick: handleOpenModal,
      class: "doctors-button",
    };

    const providerButton = {
      id: 1,
      label:
        webConfig.lang !== "fa" ? "Service Provider Login" : "ورود خدمت‌دهنده",
      onClick: pushToRoute,
      class: "patients-button",
    };

    if (showProfile) {
      return [profileButton];
    } else {
      return [profileButton, providerButton];
    }
  }, [hasMounted, user, webConfig.lang, handleOpenModal, pushToRoute]);

  const handleOpenMenu = () => {
    ModalManager.show({
      content: (
        <HamburgerMenu
          items={items}
          buttons={buttons}
          isAuth={isAuth}
          webConfig={webConfig}
        />
      ),
      title: "",
      maxWidth: "sm",
      fullScreenMobile: true,
      fullScreenDesktop: true,
      hideCloseButton: false,
      closable: true,
    });
  };

  return (
    <Wrapper
      webConfig={webConfig}
      className={isRouteChanging ? "header--loading" : ""}
    >
      <div className="header-inner">
        <div className="menu">
          <Link
            to="/"
            params={webConfig.lang !== "fa" ? { lang: webConfig.lang } : {}}
            className="logo"
          >
            <Image
              src="/nextasset/images/Logo.svg"
              width={106}
              height={40}
              alt="vclinic-logo"
            />
          </Link>

          <div className="menu desktop-menu">
            {items.map((i) => (
              <Link
                key={i.id}
                to={i.link}
                params={i.params}
                className={
                  router.pathname === i.link ? "item active-route" : "item"
                }
              >
                {i.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="row-flex">
          <div className="buttons">
            {buttons.map((b) => (
              <div
                className={`button ${b.class}`}
                key={b.id}
                onClick={b.onClick}
                suppressHydrationWarning={true}
              >
                {b.label}
              </div>
            ))}
          </div>
          <div className="menu-icon" onClick={handleOpenMenu}>
            <Image
              src="/nextasset/images/menu-close.svg"
              width={24}
              height={24}
              alt="close-icon"
            />
          </div>
          {chooseLanguage && <ChooseLanguage />}
        </div>
      </div>
      {isRouteChanging && (
        <div className="header-loading-overlay">
          <Loader color="#555" type="dots" size={16} strokeWidth={1} />
        </div>
      )}
    </Wrapper>
  );
};

export default Header;
